import { useMemo } from 'react';

import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';
import type { ConnectionType } from '~/features/onboarding/types/connection.types';

/**
 * URL `?source=` token for a browsable catalog channel. Kept in lockstep with
 * the route's `validateSearch` enum — add a token here AND there when a new
 * shopping-feed channel becomes browsable.
 */
export type CatalogSource = 'google' | 'meta';

/** Nav `code` / `path` of the backend-seeded Product Catalog group. Used by the
 *  sidebars to attach the connection-based visibility gate to the right group.
 *  The backend emits snake_case codes (`product_catalog`). */
export const PRODUCT_CATALOG_NAV_CODE = 'product_catalog';
export const PRODUCT_CATALOG_PATH = '/product-catalog';

/** True when a nav group is the Product Catalog group. Matched by code
 *  (hyphen/underscore-insensitive, so both `product_catalog` and
 *  `product-catalog` qualify) or by path. */
export function isProductCatalogNav(group: { code: string; path: string }): boolean {
  return (
    group.code.replace(/-/g, '_') === PRODUCT_CATALOG_NAV_CODE ||
    group.path === PRODUCT_CATALOG_PATH
  );
}

/**
 * Connector code → `?source=` token. Only codes listed here are treated as
 * browsable catalogs, so this map is the single extension point for the
 * Product Catalog feature (both the sidebar gate and the picker read from it).
 */
const SOURCE_BY_CODE: Partial<Record<ConnectionType, CatalogSource>> = {
  GOOGLE_MERCHANT: 'google',
  META_COMMERCE: 'meta',
};

/** Category on `/connections/connected` that owns an importable product feed. */
const CATALOG_CATEGORY = 'SHOPPING_FEEDS';

/**
 * Connector roles that expose a browsable feed. `BIDIRECTIONAL` is included
 * because it acts as both a source and a destination (the rule is "source or
 * destination").
 */
const CATALOG_CONNECTOR_TYPES = new Set(['SOURCE', 'DESTINATION', 'BIDIRECTIONAL']);

/** A connected shopping-feed catalog, annotated with its URL source token. */
export interface CatalogConnection {
  source: CatalogSource;
  connectionId: string;
  connectorCode: ConnectionType;
  name: string;
  iconUrl: string | null;
}

/**
 * Resolve the browsable catalogs from `/connections/connected`: SHOPPING_FEEDS
 * connections acting as a source/destination that map to a known `?source=`
 * channel. Drives both the catalog picker and (via `useHasProductCatalog`) the
 * sidebar visibility gate.
 */
export function useCatalogConnections(subsidiaryId?: string) {
  const query = useConnectedConnectors(subsidiaryId);

  const catalogs = useMemo<CatalogConnection[]>(() => {
    return (query.data ?? []).flatMap((row) => {
      const source = SOURCE_BY_CODE[row.code];
      if (!source) return [];
      if (row.category !== CATALOG_CATEGORY) return [];
      if (!CATALOG_CONNECTOR_TYPES.has(row.connectorType)) return [];
      return [
        {
          source,
          connectionId: row.connectionId,
          connectorCode: row.code,
          name: row.connectionName || row.name,
          iconUrl: row.iconUrl,
        },
      ];
    });
  }, [query.data]);

  return { catalogs, isLoading: query.isLoading, isError: query.isError };
}

/**
 * Sidebar-visibility gate: `true` once the tenant has at least one browsable
 * shopping-feed catalog. Reuses `useCatalogConnections`' query, so it adds no
 * extra request.
 */
export function useHasProductCatalog(subsidiaryId?: string): boolean {
  const { catalogs } = useCatalogConnections(subsidiaryId);
  return catalogs.length > 0;
}
